import { SCHEDULE_CLOSE_MINUTES, TIME_SLOTS } from "./tiers";

export function parseTime(str) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3].toUpperCase();
  if (ap === "PM" && h !== 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + min;
}

export function formatTime(minutes) {
  let h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

export function addHours(timeStr, hours) {
  const mins = parseTime(timeStr);
  if (mins == null) return "";
  return formatTime(mins + hours * 60);
}

export function formatRange(startTime, hours) {
  return `${startTime}–${addHours(startTime, hours)}`;
}

// Reads a group's schedule, supporting both the current startTime/duration
// fields and the legacy single `time` range string ("4:30 PM–6:00 PM").
// The start is floored to the hour (our start-time picker is hourly); the
// duration is then rounded up to the nearest half hour from that floored
// start, so an already hour-aligned legacy slot converts exactly and only
// slots that started on the half hour gain a little padding at the front.
export function getGroupSchedule(group) {
  if (group.startTime && group.duration) {
    return { startTime: group.startTime, duration: group.duration };
  }
  if (group.time && group.time.includes("–")) {
    const [rawStart, rawEnd] = group.time.split("–").map(s => s.trim());
    const startMin = parseTime(rawStart);
    const endMin = parseTime(rawEnd);
    if (startMin != null && endMin != null) {
      const snappedStart = Math.floor(startMin / 60) * 60;
      const duration = Math.max(1, Math.ceil((endMin - snappedStart) / 30) * 0.5);
      return { startTime: formatTime(snappedStart), duration };
    }
  }
  return { startTime: "", duration: 0 };
}

// Allowed session lengths (in hours) for a given start time and court type:
// Pickleball is always fixed at 1 hour; Tennis can run 1, 1.5, or 2 hours,
// limited by how much room is left before closing.
export function durationOptionsFor(startTime, courtType) {
  const candidates = courtType === "Pickleball" ? [1] : [1, 1.5, 2];
  if (!startTime) return candidates;
  const start = parseTime(startTime);
  const fitting = candidates.filter(h => start + h * 60 <= SCHEDULE_CLOSE_MINUTES);
  return fitting.length > 0 ? fitting : [candidates[0]];
}

export function getEndTime(group) {
  const { startTime, duration } = getGroupSchedule(group);
  if (!startTime) return "";
  return addHours(startTime, duration);
}

// The 1-hour TIME_SLOTS labels (e.g. "4:00 PM–5:00 PM") a [startTime, +duration)
// range overlaps — used for availability/junior-eligibility checks, which
// only reason in whole-hour terms even though the actual range can include
// a trailing half hour or an off-the-hour start.
export function getOccupiedSlotsForRange(startTime, duration) {
  if (!startTime || !duration) return [];
  const start = parseTime(startTime);
  const end = start + duration * 60;
  return TIME_SLOTS.filter(slot => {
    const [s, e] = slot.split("–");
    return parseTime(s) < end && parseTime(e) > start;
  });
}

export function getOccupiedSlots(group) {
  const { startTime, duration } = getGroupSchedule(group);
  return getOccupiedSlotsForRange(startTime, duration);
}
